// WHAT: Form component for managing Supabase configuration (URL and API key)
// WHY:  Provides interface for setting Supabase connection credentials
// DOMAIN: Settings → Supabase
// DEPENDENCIES: useSupabaseConfig hook, setSupabaseConfig logic, resetSupabaseClient
// RISKS:
//   - NOTE: API key is displayed in plain text - security risk
//   - NOTE: No validation for URL or key format - may allow invalid credentials
//   - NOTE: Client reset may cause issues if other components are using it

import { Database, CheckCircle, AlertCircle, Loader2, Key, Globe } from "lucide-react";
import { useState, useEffect } from "react";

import { Card, Button, Input, Label, Stack } from "@/components/ui";
import { resetSupabaseClient } from "@/shared/services";

import { useSupabaseConfig } from "../hooks/useSupabaseConfig";
import { setSupabaseConfig } from "../logic/saveSupabaseToStore";
import { type SupabaseConfig } from "../types/SupabaseConfig";

/**
 * WHAT:
 *   Form component that provides interface for managing Supabase configuration
 *   with URL and API key inputs.
 *
 * WHY:
 *   Enables users to configure Supabase connection for the application.
 *
 * BEHAVIOR:
 *   - Loads current configuration on mount
 *   - Allows editing URL and API key
 *   - Saves configuration to secure storage
 *   - Resets Supabase client after save
 *   - Shows success/error status
 *
 * RISKS:
 *   - NOTE: API key is displayed in plain text - security risk
 *   - NOTE: No validation for URL or key format - may allow invalid credentials
 *   - NOTE: Client reset may cause issues if other components are using it
 *
 * @returns {JSX.Element} Supabase configuration form component
 *
 */
export function SupabaseConfigForm() {
  const { config: loadedConfig, loading, refetch } = useSupabaseConfig();
  const [config, setConfig] = useState<SupabaseConfig>({
    key: "",
    url: "",
  });
  const [status, setStatus] = useState<"error" | "idle" | "success">("idle");
  const [saving, setSaving] = useState(false);

  // Load config when component mounts or when loadedConfig changes
  useEffect(() => {
    if (loadedConfig) {
      setConfig(loadedConfig);
    }
  }, [loadedConfig]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setStatus("idle");

    try {
      await setSupabaseConfig(config);
      resetSupabaseClient();
      setStatus("success");
      // Refetch to ensure UI is in sync
      refetch();
    } catch {
      // NOTE: Supabase config save failed - error message already set
      setStatus("error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="bg-slate-800/50 border-slate-700/50" padding="lg" variant="default">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-tracq-bright-blue/10 rounded-lg">
            <Database className="w-5 h-5 text-tracq-bright-blue" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Supabase Configuration</h3>
            <p className="text-sm text-slate-400">
              Configure your Supabase project URL and API key
            </p>
          </div>
        </div>

        {/* Form */}
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            <span className="ml-2 text-slate-400">Loading configuration...</span>
          </div>
        ) : (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              void handleSubmit(e);
            }}
          >
            <Stack direction="col" gap={4}>
              {/* Supabase URL */}
              <div className="space-y-2">
                <Label
                  className="text-slate-300 flex items-center space-x-2"
                  htmlFor="supabase-url"
                >
                  <Globe className="w-4 h-4" />
                  <span>Supabase URL</span>
                </Label>
                <Input
                  className="bg-slate-700 border-slate-600 text-white focus:border-tracq-bright-blue focus:ring-tracq-bright-blue/20"
                  id="supabase-url"
                  onChange={(e) => {
                    setConfig({ ...config, url: e.target.value });
                  }}
                  placeholder="https://your-project.supabase.co"
                  required
                  type="url"
                  value={config.url}
                />
                <p className="text-xs text-slate-500">
                  Your Supabase project URL (found in Project Settings → API)
                </p>
              </div>

              {/* Supabase Key */}
              <div className="space-y-2">
                <Label
                  className="text-slate-300 flex items-center space-x-2"
                  htmlFor="supabase-key"
                >
                  <Key className="w-4 h-4" />
                  <span>Supabase API Key</span>
                </Label>
                <Input
                  className="bg-slate-700 border-slate-600 text-white focus:border-tracq-bright-blue focus:ring-tracq-bright-blue/20"
                  id="supabase-key"
                  onChange={(e) => {
                    setConfig({ ...config, key: e.target.value });
                  }}
                  placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
                  required
                  type="password"
                  value={config.key}
                />
                <p className="text-xs text-slate-500">
                  Your Supabase anon/public API key (found in Project Settings → API)
                </p>
              </div>
            </Stack>

            {/* Save Button */}
            <Button
              className="w-full sm:w-auto"
              disabled={saving || !config.url || !config.key}
              size="md"
              type="submit"
              variant="primary"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Save Configuration
                </>
              )}
            </Button>
          </form>
        )}

        {/* Status Messages */}
        {status === "success" && (
          <div className="flex items-center space-x-2 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
            <CheckCircle className="w-5 h-5 text-green-500" />
            <span className="text-green-400 text-sm font-medium">
              Supabase configuration saved successfully!
            </span>
          </div>
        )}

        {status === "error" && (
          <div className="flex items-center space-x-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <span className="text-red-400 text-sm font-medium">
              Error saving configuration. Please check your credentials.
            </span>
          </div>
        )}

        {/* Info Card */}
        <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
          <div className="flex items-start space-x-2">
            <AlertCircle className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-medium text-blue-400 mb-1">Security Note</h4>
              <p className="text-xs text-blue-300">
                Your Supabase credentials are stored locally and securely. Never share your API keys
                publicly.
              </p>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}
